import React from "react";
import {
  PieChart as RePieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import formatNumber from "../../../helpers/formatNumber";
import truncate from "../../../helpers/truncateString";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;

  const entry = payload[0];
  return (
    <div className="bg-dark text-white p-2 rounded shadow-sm border border-secondary">
      <strong>{entry.name}</strong>
      <div>
        Wert: {formatNumber(entry.payload.absolute) ?? "–"} € (
        {entry.value.toFixed(2)}%)
      </div>
    </div>
  );
};

export default function PieChart({
  dataset = [],
  colors = [],
  startPosition = 0,
  endPosition = 360,
  innerRadius = "0%",
  label,
  metaLabel,
  animated = true,
  className,
  showActiveKey = false,
}) {
  const [activeIndex, setActiveIndex] = React.useState(null);
  // dataset erwartet [{ name: "Apple", value: 5000 }, { name: "Tesla", value: 3000 }]
  const totalValue = dataset.reduce((sum, stock) => sum + stock.value, 0);

  //* Prozentwerte für Chart
  const dataWithPercent = dataset.map((stock) => ({
    name: stock.name,
    value: (stock.value / totalValue) * 100, // für Chart (Prozent)
    absolute: stock.value, // für Tooltip (Euro)
  }));

  //* active entry
  const activeEntry =
    activeIndex !== null ? dataWithPercent[activeIndex] : null;

  React.useEffect(() => {
    setActiveIndex(null);
  }, [dataset]);

  return (
    <div className={`pie-chart-container ${className}`}>
      <ResponsiveContainer width="100%" height="90%">
        <RePieChart onClick={() => setActiveIndex(null)}>
          <Pie
            data={dataWithPercent}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius="100%"
            innerRadius={innerRadius}
            isAnimationActive={animated}
            stroke="none"
            paddingAngle={0.5}
            startAngle={startPosition}
            endAngle={endPosition}
            onClick={(_, index, event) => {
              showActiveKey && event?.stopPropagation();
              showActiveKey &&
                setActiveIndex(index === activeIndex ? null : index);
            }}
          >
            {dataWithPercent.map((entry, idx) => (
              <Cell
                key={`cell-${idx}`}
                fill={colors[idx % colors.length]}
                opacity={activeIndex === null || activeIndex === idx ? 1 : 0.1}
                style={{ cursor: "pointer" }}
              />
            ))}
          </Pie>
          {label && metaLabel && (
            <>
              <text
                x="50%"
                y="50%"
                dy="-10"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="24"
                fontWeight="bold"
                fill="lawngreen"
                onClick={() => setActiveIndex(null)}
              >
                {activeEntry
                  ? `${formatNumber(activeEntry.absolute)} €`
                  : label}
              </text>
              <text
                x="50%"
                y="50%"
                dy="-32"
                textAnchor="middle"
                fontSize="14"
                fill="#888"
              >
                {activeEntry ? truncate(activeEntry?.name) : metaLabel}
              </text>
            </>
          )}
          {(!label || !metaLabel) && <Tooltip content={<CustomTooltip />} />}
        </RePieChart>
      </ResponsiveContainer>
    </div>
  );
}
